// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import React from 'react';
import {get} from 'lodash';

import {MessageWithDisambiguation} from '../message-disambiguation';
import {Message} from '../message';
import {MessageAnswer} from '../message-answer';
import {MessageUserChoice} from '../message-user-choice';
import {FeedbackChoice} from '../feedback-choice';
import {MessageResolutionMissing} from '../message-resolution-missing';
import {MessageImage} from '../message-image';
import {MessageVideo} from '../message-video';
import {WebExtMessage} from '../message-webext';
import {MessageCTA} from '../message-cta';
import {MessageQuote} from '../message-quote';
import {MessageCX} from '../message-cx';
import {MessageAttachment} from '../message-attachment';
import {MessageRTA} from '../message-rta';

export const NORMAL_MESSAGE = 'normal';
export const DISAMBIGUATION_MESSAGE = 'resolution/disambiguation';
export const WEBEXT_MESSAGE = 'resolution/answer/webext';
export const HANDOVER_MESSAGE = 'resolution/answer/handover';
export const CTA_MESSAGE = 'resolution/answer/call-to-action';
export const ANSWER_MESSAGE = 'resolution/answer';
export const RESOLUTION_MISSING = 'resolution/missing';
export const CHOICE_FEEDBACK_UP = 'choice/feedback_up';
export const CHOICE_FEEDBACK_DOWN = 'choice/feedback_down';
export const CHOICE_RESPONSE = 'choice/response';
export const CHOICE_RESOLUTION = 'choice/resolution';
export const CHOICE_NOT_WHAT_I_ASKED = 'choice/ko_not_what_i_asked';
export const CHOICE_NOT_RESOLVED = 'choice/ko_not_resolved';
export const META_BEACON = 'meta/beacon';
export const CHOICE_THATS_ALL = 'choice/ok_thats_all';
export const CHOICE_ESCALATED = 'choice/escalate';
export const ANSWER_CHOICE = 'resolution/answer/choice';
export const ANSWER_IMAGE = 'resolution/answer/image';
export const ANSWER_VIDEO = 'resolution/answer/video';
export const ANSWER_QUOTE = 'resolution/answer/quote';
export const ANSWER_ATTACHMENTS = 'resolution/answer/attachments';
export const ANSWER_FEEDBACK = 'resolution/answer/feedback';
export const OPEN_DOCUMENT = 'open/document';
export const OPEN_WEBEXT = 'open/webext';
export const OPEN_RESPONSE = 'open/response';
export const OPEN_LINK = 'open/link';
export const IN_HI = 'in/hi';
export const IN_MESSAGE = 'in/message';
export const NPS = 'nps';
export const CSAT = 'csat';
export const CES = 'ces';

const getMessageType = (message) => {
    return get(
        message,
        'aspect',
        get(message, 'target.aspect', NORMAL_MESSAGE),
    );
};

const getIsResponse = (message) => {
    return get(message, 'headers.resource_types', '') === 'response';
};

const getResourceType = (message) => {
    return get(message, 'headers.resource_types', '');
};

// Renders a message block with different styles depending on the messageType.
export const getMessageComponent = (props: MessageComponentProps) => {
    const {message, messages} = props;
    const messageType = getMessageType(message) || message.uri;
    const isResponse = getIsResponse(message);
    const typeMessage = getResourceType(message);

    switch (messageType) {
        case 'fab':
            if (message.uri && message.uri === 'meta/beacon') {
                return (
                    <div className="message-user-choice__container console-activity-message-container">
                        <div className="console-activity-message message-user-choice message-user-choice__response">
                            <p>Navigated</p>
                            <p className="message-user-choice__default">
                                {message.referral}
                            </p>
                        </div>
                    </div>
                );
            }
            return null;

        case CTA_MESSAGE:
            return <MessageCTA {...props} />;
        case WEBEXT_MESSAGE:
        case OPEN_WEBEXT:
        case HANDOVER_MESSAGE:
            return <WebExtMessage {...props} />;
        case DISAMBIGUATION_MESSAGE:
        case ANSWER_CHOICE:
            return <MessageWithDisambiguation {...props} />;
        case ANSWER_MESSAGE:
            return (
                <MessageAnswer
                    {...props}
                    isResponse={isResponse}
                    typeMessage={typeMessage}
                />
            );
        case ANSWER_VIDEO:
            return <MessageVideo {...props} />;
        case ANSWER_IMAGE:
            return <MessageImage {...props} />;
        case ANSWER_QUOTE:
            return <MessageQuote {...props} />;
        case CHOICE_FEEDBACK_UP:
        case CHOICE_FEEDBACK_DOWN:
        case CHOICE_RESPONSE:
        case CHOICE_RESOLUTION:
        case OPEN_DOCUMENT:
        case CHOICE_NOT_WHAT_I_ASKED:
        case CHOICE_NOT_RESOLVED:
        case CHOICE_THATS_ALL:
        case CHOICE_ESCALATED:
        case OPEN_LINK:
            return (
                <MessageUserChoice
                    {...props}
                    isResponse={isResponse}
                    typeMessage={typeMessage}
                />
            );
        case RESOLUTION_MISSING:
            return <MessageResolutionMissing {...props} />;
        case ANSWER_FEEDBACK:
            return <FeedbackChoice {...props} />;
        case ANSWER_ATTACHMENTS:
            return <MessageAttachment {...props} />;
        case NPS:
        case CSAT:
        case CES: {
            if (message.uri === 'in/cx/answer') {
                return <MessageCX {...props} />;
            } else if (message.uri === 'out/cx/question') {
                const cxMetricHasAnswer = !!messages?.find(
                    (msg) =>
                        msg.parent_id === message.id &&
                        msg.id !== message.id &&
                        msg.uri === 'in/cx/answer',
                );
                if (!cxMetricHasAnswer) return <MessageCX {...props} />;
            }
            return null;
        }
        case IN_MESSAGE: {
            if (message.body.trigger_method === 'rta') {
                return <MessageRTA {...props} />;
            }
            return null;
        }
        default:
            return <Message {...props} />;
    }
};

interface MessageComponentProps {
    message: any;
    messages: any[];
    metricId: string;
    type: string;
    messageType: string;
    isResponse: boolean;
    typeMessage: string;
    isHovering: any;
    allMessages: any[];
    onMouseEnter: any;
    onMouseLeave: any;
    requiredPermissions: any[];
    scopes: any[];
    resetCache: any;
}
